import React from 'react'
import { createNgReactBridgeDirective } from '../../react-bridge'
import {
  Severity,
  VulnerabilitySeverityBadge,
  VulnerabilitySeverityChangeDropdown
} from '../../../components/vulnerability-severity'
import { getMostPermissive, PrefixedRole } from '../../bridge-utils/roles'
import { RoleSwitch } from '../../../components/roles'
import { css } from '@emotion/css'

export type NgSeverityName = 'critical' | 'high' | 'medium' | 'low' | 'note'
export type SeverityName = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NOTE'
const severityNameMap = new Map<NgSeverityName, SeverityName>([
  ['critical', 'CRITICAL'],
  ['high', 'HIGH'],
  ['medium', 'MEDIUM'],
  ['low', 'LOW'],
  ['note', 'NOTE']
])

export interface Props {
  readonly applicationRoles: PrefixedRole[]
  readonly organizationID: string
  readonly vulnerabilityID: string
  readonly severityName: string
  readonly defaultSeverityName: string
  readonly impactName: string
  readonly likelihoodName: string
  readonly refresh: () => Promise<any>
}

const mapSeverityName = (key: string, val: string) => {
  const mapped = severityNameMap.get(val.toLowerCase() as NgSeverityName)
  if (!mapped) {
    throw new Error(`${key} is required`)
  }
  return mapped
}

function mapProps({
  applicationRoles,
  severityName,
  defaultSeverityName,
  impactName,
  likelihoodName,
  refresh,
  ...props
}: Props) {
  const mappedSeverityName = mapSeverityName('severityName', severityName)
  const mappedDefaultSeverityName = mapSeverityName(
    'defaultSeverityName',
    defaultSeverityName
  )
  const mappedImpactName = mapSeverityName('impactName', impactName)
  const mappedLikelihoodName = mapSeverityName('likelihoodName', likelihoodName)
  return {
    severity: Severity.fromName(mappedSeverityName),
    defaultSeverity: Severity.fromName(mappedDefaultSeverityName),
    impact: Severity.fromName(mappedImpactName),
    likelihood: Severity.fromName(mappedLikelihoodName),
    refresh: async () => refresh(),
    role: getMostPermissive(applicationRoles),
    refetchQueries: [],
    ...props
  }
}

const bridgeContainerCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const BridgeComponent = (props: Props) => {
  const { role, ...mappedProps } = mapProps(props)
  return (
    <div className={bridgeContainerCss}>
      <RoleSwitch
        role={role}
        requiredRole="RULES_ADMIN"
        renderAllowed={() => (
          <VulnerabilitySeverityChangeDropdown {...mappedProps} />
        )}
        renderDenied={() => <VulnerabilitySeverityBadge {...mappedProps} />}
      />
    </div>
  )
}

export const vulnerabilitySeverityBadge = createNgReactBridgeDirective(
  'reactVulnerabilitySeverityBadge',
  [],
  BridgeComponent,
  [
    'applicationRoles',
    'organizationID',
    'vulnerabilityID',
    'likelihoodName',
    'impactName',
    'severityName',
    'defaultSeverityName',
    'refresh'
  ]
)
